import React, { FC, useCallback, useMemo } from "react";
import { Select, Tooltip } from "antd";
import { GetRef } from "antd";
import Loader from "../../../../common/loader/Loader";
import SelectEmptyState from "../../../../metadata/components/categorization/SelectEmptyState";
import useDelayedFlag from "../../../../metadata/hooks/useDelayedFlag";
import { VALUE_LOADING_INDICATOR_DELAY_MS } from "../../../../metadata/constants";

import { OptionType } from "../../../../metadata/interfaces/classifier";
import "../../tableCategorizationCell/CategorizationCell.scss";

type SelectRef = GetRef<typeof Select>;

type Props = {
  disabled: boolean;
  selectedCategory: {
    value: string | undefined;
    visibleValue: string | undefined;
  } | null;
  categorizationOptions: Array<OptionType>;
  handleChange: (value: string, callback: () => void) => void;
  isCategorizationDisabledByParent: boolean | undefined;
  disabledParentTooltip: string;
  isIncomplete: boolean;
  emptySearchResult: boolean;
  loading: boolean;
  isLoadingValue: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onSearch: (value: string) => void;
  // Matches antd's `FilterFunc`, which declares the option as optional.
  filterOption: (input: string, option?: OptionType) => boolean;
  className: string;
  selectRef: React.RefObject<SelectRef | null>;
  onKeyDown: (e: React.KeyboardEvent) => void;
  /** Show the option label instead of the stored value (`search.displayLabel`). */
  displayLabel: boolean;
};

const { Option } = Select;
const mapping = { value: "key" };

const CategorizationCellUI: FC<Props> = ({
  disabled,
  selectedCategory,
  categorizationOptions,
  handleChange,
  isCategorizationDisabledByParent,
  disabledParentTooltip,
  emptySearchResult,
  loading,
  isLoadingValue,
  onFocus,
  onBlur,
  onSearch,
  filterOption,
  className,
  selectRef,
  onKeyDown,
  displayLabel,
}) => {
  const tooltipValue = useMemo(() => {
    if (isCategorizationDisabledByParent) {
      return disabledParentTooltip;
    }

    if (!selectedCategory) {
      return null;
    }

    if (selectedCategory.visibleValue) {
      return (
        <>
          <div>
            <strong>Name: </strong>
            {selectedCategory.visibleValue}
          </div>
          <div>
            <strong>Value: </strong>
            {selectedCategory.value}
          </div>
        </>
      );
    }

    return selectedCategory.value;
  }, [
    selectedCategory,
    isCategorizationDisabledByParent,
    disabledParentTooltip,
  ]);

  const showValueLoading = useDelayedFlag(
    isLoadingValue,
    VALUE_LOADING_INDICATOR_DELAY_MS,
  );

  // The Select uses `fieldNames={{ value: "key" }}` so it matches options by
  // their `key` (UUID). `selectedCategory.value` is the annotation's value
  // (e.g. "628"), so translate it to the corresponding option key for the
  // match to succeed and the option's label to be displayed.
  const selectValue = useMemo(() => {
    if (selectedCategory?.value == null) {
      return undefined;
    }
    const found = categorizationOptions?.find(
      (o) => o.value === selectedCategory.value,
    );
    return found?.key ?? selectedCategory.value;
    // Depending on `selectedCategory` rather than `selectedCategory?.value` only
    // makes this recompute more often; the result is a string derived purely
    // from that value, so the `value` passed to the Select is unchanged.
  }, [categorizationOptions, selectedCategory]);

  // By default the closed select shows the stored value (e.g. a customer
  // number), with visibleValue as a fallback. With `displayLabel` the value is
  // a bare record id (e.g. "14"), so prefer the matched option's label; the
  // fallbacks cover the window before the lazily-fetched options arrive.
  // `props.value` is last because `fieldNames` maps it to the option key, a UUID.
  const renderLabel = useCallback(
    (props: { label?: React.ReactNode; value?: string | number }) => {
      if (displayLabel) {
        return (
          props.label ??
          selectedCategory?.visibleValue ??
          selectedCategory?.value ??
          props.value
        );
      }
      return (
        selectedCategory?.value ?? selectedCategory?.visibleValue ?? props.value
      );
    },
    [displayLabel, selectedCategory?.value, selectedCategory?.visibleValue],
  );

  return (
    <div className="categorization-item__container">
      <Tooltip placement="top" title={tooltipValue}>
        <Select
          ref={selectRef}
          title=""
          allowClear
          showSearch
          value={selectValue}
          labelRender={renderLabel}
          suffixIcon={showValueLoading ? <Loader size="small" /> : undefined}
          placeholder={showValueLoading ? "Loading..." : "Select a value"}
          optionFilterProp="children"
          onChange={(value) => handleChange(value, selectRef.current!.blur)}
          onFocus={onFocus}
          onBlur={onBlur}
          onSearch={onSearch}
          filterOption={filterOption}
          loading={loading}
          notFoundContent={
            <SelectEmptyState
              loading={loading || isLoadingValue}
              hasNoResult={emptySearchResult}
            />
          }
          fieldNames={mapping}
          disabled={disabled || isCategorizationDisabledByParent}
          className={`${className} ${
            loading ? "loading-select-prominent" : ""
          }`}
          popupMatchSelectWidth={false}
          onKeyDown={onKeyDown}
        >
          {categorizationOptions?.map((o) => {
            const { label, value, key } = o;
            const split = label.split(/\n/);

            if (split.length > 1) {
              return (
                <Option key={key} value={value} label={label}>
                  <ul>
                    {split.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </Option>
              );
            }

            return (
              <Option key={key} value={value} label={label}>
                {o.label}
              </Option>
            );
          })}
        </Select>
      </Tooltip>
    </div>
  );
};

export default CategorizationCellUI;
